import api from "@/api";

// Ícones que acompanham o bundle. Continuam aqui de propósito: as redes
// migradas do código para o banco nascem sem ícone próprio (as imagens sempre
// viveram neste repositório, não no storage do Laravel). Quando o admin envia
// um ícone, a URL vinda da API tem prioridade sobre estes.
import instagramHeader from "assets/img/instagram.png";
import faceHeader from "assets/img/face.webp";
import twitterHeader from "assets/img/twitter.webp";
import tiktokHeader from "assets/img/tiktok.webp";
import topfansHeader from "assets/img/topfans.webp";
import telegramHeader from "assets/img/telegram.webp";
import whatsappHeader from "assets/img/whatsapp.webp";

import instagramMenu from "assets/img/instagramRed.png";
import faceMenu from "assets/img/faceRed.webp";
import twitterMenu from "assets/img/twitterRed.png";
import tiktokMenu from "assets/img/tiktokRed.webp";
import topfansMenu from "assets/img/topfansRed.png";
import telegramMenu from "assets/img/telegramRed.png";
import whatsappMenu from "assets/img/whatsappRed.png";

import instagramFooter from "assets/img/instagramRedBackBlack.png";
import faceFooter from "assets/img/faceRedBackBlack.webp";
import twitterFooter from "assets/img/twitterRedBackBlack.webp";
import tiktokFooter from "assets/img/tiktokRedBackBlack.webp";
import topfansFooter from "assets/img/topfansRedBackBlack.webp";
import telegramFooter from "assets/img/telegramRedBackBlack.webp";
import whatsappFooter from "assets/img/whatsappRedBackBlack.webp";

export type SocialPlacement = "header" | "menu" | "footer";

/** Formato devolvido por GET /api/v1/social-networks/getActive */
export interface SocialNetworkApiItem {
  slug: string;
  name: string;
  url: string;
  placements: SocialPlacement[];
  icons: Partial<Record<SocialPlacement, string | null>>;
}

/** Item já resolvido para um local específico, pronto para renderizar. */
export interface SocialNetworkIcon {
  slug: string;
  name: string;
  url: string;
  icon: string;
}

const BUNDLED_ICONS: Record<string, Record<SocialPlacement, string>> = {
  instagram: { header: instagramHeader, menu: instagramMenu, footer: instagramFooter },
  facebook: { header: faceHeader, menu: faceMenu, footer: faceFooter },
  twitter: { header: twitterHeader, menu: twitterMenu, footer: twitterFooter },
  tiktok: { header: tiktokHeader, menu: tiktokMenu, footer: tiktokFooter },
  topfans: { header: topfansHeader, menu: topfansMenu, footer: topfansFooter },
  telegram: { header: telegramHeader, menu: telegramMenu, footer: telegramFooter },
  whatsapp: { header: whatsappHeader, menu: whatsappMenu, footer: whatsappFooter },
};

/**
 * Busca as redes sociais cadastradas no admin.
 *
 * A chave do useAsyncData é a mesma nos três componentes que consomem isto
 * (Header, Navbar e Footer), então o Nuxt faz UMA requisição por render e
 * serializa o resultado no payload do SSR — sem refetch na hidratação.
 *
 * Não existe lista estática de reserva: o que o admin cadastrar é o que
 * aparece, e nada cadastrado significa nenhum ícone no site. Mesma escolha
 * que o useSeoConfig já fez ("o backend é o responsável") — uma cópia
 * paralela das URLs aqui envelheceria em silêncio a cada edição no painel.
 */
export async function useSocialNetworks(language: string) {
  const key = `social-networks-${language}`;

  const asyncData = useAsyncData(key, async () => {
    try {
      const res = await api.get("v1/social-networks/getActive", {
        params: { language },
      });

      const networks = res.data?.networks as SocialNetworkApiItem[] | undefined;

      return Array.isArray(networks) ? networks : [];
    } catch {
      return [];
    }
  });

  await asyncData; // bloqueia o SSR até resolver (evita os ícones "pipocarem")

  return asyncData.data;
}

/**
 * Filtra as redes de um local e resolve o ícone, dando prioridade ao que foi
 * enviado pelo admin. Redes sem ícone em lugar nenhum são descartadas para não
 * renderizar imagem quebrada.
 */
export function socialNetworksFor(
  networks: SocialNetworkApiItem[] | null | undefined,
  placement: SocialPlacement
): SocialNetworkIcon[] {
  if (!networks) {
    return [];
  }

  return networks
    .filter((network) => network.placements?.includes(placement))
    .map((network) => ({
      slug: network.slug,
      name: network.name,
      url: network.url,
      icon: network.icons?.[placement] || BUNDLED_ICONS[network.slug]?.[placement] || "",
    }))
    .filter((network) => network.icon !== "");
}
